import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment/min/moment-with-locales';
import { getLangKey } from 'common/src/other/getLangKey';

const WATERMARK = 'movex.com.co';
const BRAND_GREEN = [6, 193, 103];
const BRAND_DARK = [20, 20, 20];
const MUTED = [100, 116, 139];

function fmt(value, settings) {
  const number = parseFloat(value || 0);
  const decimal = settings?.decimal != null ? settings.decimal : 0;
  const country = settings?.country;
  if (country === 'Vietnam') {
    return number.toLocaleString('vi-VN', { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
  }
  return number.toLocaleString('en-US', { minimumFractionDigits: decimal, maximumFractionDigits: decimal });
}

function symbolize(value, settings) {
  if (value == null || value === '') return '—';
  const amount = fmt(value, settings);
  if (!settings?.symbol) return amount;
  return settings.swipe_symbol ? `${amount} ${settings.symbol}` : `${settings.symbol} ${amount}`;
}

function cell(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

function drawWatermark(doc) {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.saveGraphicsState();
    try {
      doc.setGState(doc.GState({ opacity: 0.07 }));
    } catch (e) {
      doc.setTextColor(230, 230, 230);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(42);
    doc.setTextColor(180, 180, 180);
    doc.text(WATERMARK, w / 2, h / 2, { align: 'center', angle: 35 });
    doc.restoreGraphicsState();
  }
}

function drawHeader(doc, settings, t, booking) {
  const company = settings?.CompanyName || 'MOVEX';
  const margin = 14;
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(company, margin, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(WATERMARK, margin, 19);
  doc.setFontSize(8);
  doc.text(
    t('booking_details_pdf_subtitle', { defaultValue: 'Detalle de reserva' })
      + ' · ' + moment().format('LLL'),
    margin,
    24
  );
  doc.setTextColor(...BRAND_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  const ref = booking?.reference || booking?.id || '';
  doc.text(
    t('booking_details_pdf_title', { defaultValue: 'Reserva' }) + (ref ? ` ${ref}` : ''),
    margin,
    38
  );
  return 44;
}

function drawSection(doc, startY, title, rows, options = {}) {
  const margin = 14;
  let y = startY;
  const pageH = doc.internal.pageSize.getHeight();

  if (y > pageH - 40) {
    doc.addPage();
    y = 18;
  }

  doc.setFillColor(...BRAND_GREEN);
  doc.roundedRect(margin, y, doc.internal.pageSize.getWidth() - margin * 2, 8, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(title, margin + 4, y + 5.5);
  y += 12;

  autoTable(doc, {
    startY: y,
    head: [],
    body: rows.map(([label, value]) => [label, value]),
    theme: 'plain',
    styles: {
      fontSize: 9,
      cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 72, fontStyle: 'bold', textColor: MUTED },
      1: { cellWidth: 'auto' },
    },
    didParseCell: (hookData) => {
      if (options.highlight && hookData.section === 'body' && hookData.column.index === 1) {
        const key = rows[hookData.row.index]?.[0];
        if (key === options.highlight.pickup) {
          hookData.cell.styles.textColor = [22, 163, 74];
          hookData.cell.styles.fontStyle = 'bold';
        }
        if (key === options.highlight.drop) {
          hookData.cell.styles.textColor = [220, 38, 38];
          hookData.cell.styles.fontStyle = 'bold';
        }
      }
    },
    margin: { left: margin, right: margin },
  });

  return (doc.lastAutoTable?.finalY || y) + 8;
}

function buildSections(booking, settings, t, role) {
  const isBid = role === 'customer' && ['NEW', 'PAYMENT_PENDING'].indexOf(booking?.status) !== -1;
  const hideContact = !settings?.AllowCriticalEditsAdmin;

  const tripRows = [
    [t('booking_id'), cell(booking?.id)],
    [t('booking_ref'), cell(booking?.reference)],
    [t('booking_status_web'), cell(t(booking?.status))],
    [t('trip_start_date'), booking?.tripdate ? moment(booking.tripdate).format('lll') : '—'],
    [t('trip_start_time'), cell(booking?.trip_start_time)],
    [t('trip_end_time'), cell(booking?.trip_end_time)],
    [t('otp'), booking?.otp ? t('true') : t('false')],
    [t('roundTrip'), booking?.roundTrip ? t('yes') : t('no')],
    [t('total_time'), cell(booking?.total_trip_time)],
    [
      t('distance_web'),
      booking?.distance
        ? (settings?.convert_to_mile ? `${booking.distance} ${t('mile')}` : `${booking.distance} ${t('km')}`)
        : '—',
    ],
    [t('you_rated_text'), booking?.rating ? `${booking.rating} / 5` : '—'],
    [t('feedback'), cell(booking?.feedback)],
    [t('tripInstructions'), cell(booking?.tripInstructions)],
    [t('pickUpInstructions_web'), cell(booking?.pickUpInstructions)],
    [t('deliveryInstructions'), cell(booking?.deliveryInstructions)],
    [t('cancellation_reason'), booking?.reason ? t(getLangKey(booking.reason)) : '—'],
    [t('otherPerson'), cell(booking?.otherPerson)],
    [t('otherPersonPhone'), cell(booking?.otherPersonPhone)],
    [t('pickup_address'), cell(booking?.pickupAddress)],
    [t('drop_address'), cell(booking?.dropAddress)],
  ];

  const paymentRows = [
    [t('trip_cost'), isBid ? t('bid').toUpperCase() : symbolize(booking?.trip_cost, settings)],
    [t('Customer_paid'), symbolize(booking?.customer_paid, settings)],
    [t('discount_ammount'), symbolize(booking?.discount, settings)],
    [t('driver_share'), isBid ? '—' : symbolize(booking?.driver_share, settings)],
    [
      t('fleet_admin_comission'),
      booking?.fleetCommission && parseFloat(booking.fleetCommission) > 0
        ? symbolize(booking.fleetCommission, settings)
        : '—',
    ],
    [t('convenience_fee'), isBid ? '—' : symbolize(booking?.convenience_fees, settings)],
    [t('tipamount'), booking?.tipamount ? symbolize(booking.tipamount, settings) : '—'],
    [t('cancellationFee'), symbolize(booking?.cancellationFee ?? 0, settings)],
    [t('payment_gateway'), cell(booking?.gateway)],
    [t('payment_mode_web'), cell(booking?.payment_mode)],
    [t('cash_payment_amount'), symbolize(booking?.cashPaymentAmount, settings)],
    [t('card_payment_amount'), symbolize(booking?.cardPaymentAmount, settings)],
    [t('wallet_payment_amount'), symbolize(booking?.usedWalletMoney, settings)],
    [t('payable_ammount'), symbolize(booking?.payableAmount, settings)],
  ];

  const driverRows = [
    [t('driver_id'), cell(booking?.driver)],
    [t('driver_name'), cell(booking?.driver_name)],
    [
      t('driver_contact'),
      booking?.driver_contact ? (hideContact ? t('hidden_demo') : booking.driver_contact) : '—',
    ],
    [
      t('driver_email'),
      booking?.driver_email ? (hideContact ? t('hidden_demo') : booking.driver_email) : '—',
    ],
    [t('car_type'), booking?.carType ? t(getLangKey(booking.carType)) : '—'],
    [t('vehicle_no'), cell(booking?.vehicle_number)],
    [t('device_id'), cell(booking?.driverDeviceId)],
    [t('fleetadmins'), role === 'admin' ? cell(booking?.fleetadmin) : '—'],
  ];

  const customerRows = [
    [t('customer_id'), cell(booking?.customer)],
    [t('customer_name'), cell(booking?.customer_name)],
    [
      t('customer_contact'),
      booking?.customer_contact ? (hideContact ? t('hidden_demo') : booking.customer_contact) : '—',
    ],
    [
      t('customer_email'),
      booking?.customer_email ? (hideContact ? t('hidden_demo') : booking.customer_email) : '—',
    ],
  ];

  const sections = [
    { title: t('ride_information'), rows: tripRows, highlight: { pickup: t('pickup_address'), drop: t('drop_address') } },
    { title: t('payment_info'), rows: paymentRows },
    { title: t('driver_info'), rows: driverRows },
    { title: t('customer_info'), rows: customerRows },
  ];

  if (booking?.evidence) {
    ['pickup', 'drop'].forEach((ph) => {
      const ev = booking.evidence[ph];
      if (!ev) return;
      const title = ph === 'pickup'
        ? t('evidence_title_pickup', { defaultValue: 'Evidencia de recogida' })
        : t('evidence_title_drop', { defaultValue: 'Evidencia de entrega' });
      const rows = [
        [t('vehicle_plate', { defaultValue: 'Placa' }), cell(ev.plate)],
        [t('notes', { defaultValue: 'Notas' }), cell(ev.notes)],
        [
          t('photos', { defaultValue: 'Fotos' }),
          (ev.photos || []).length ? (ev.photos || []).join('\n') : '—',
        ],
      ];
      sections.push({ title, rows });
    });
  }

  if (booking?.pickup_image) {
    sections.push({
      title: t('take_pickup_image_web'),
      rows: [[t('image_url', { defaultValue: 'URL' }), booking.pickup_image]],
    });
  }
  if (booking?.deliver_image) {
    sections.push({
      title: t('take_deliver_image_web'),
      rows: [[t('image_url', { defaultValue: 'URL' }), booking.deliver_image]],
    });
  }

  return sections;
}

export function generateBookingDetailsPDF(booking, settings, t, role) {
  if (!booking) return;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = drawHeader(doc, settings, t, booking);

  const sections = buildSections(booking, settings, t, role);
  sections.forEach((section) => {
    y = drawSection(doc, y, section.title, section.rows, { highlight: section.highlight });
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      y = 18;
    }
  });

  const margin = 14;
  const footerY = doc.internal.pageSize.getHeight() - 10;
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(
    `${WATERMARK} · ${t('confidential_document', { defaultValue: 'Documento interno Movex' })}`,
    margin,
    footerY
  );

  drawWatermark(doc);

  const ref = (booking.reference || booking.id || 'reserva').replace(/[^\w-]/g, '_');
  doc.save(`movex_reserva_${ref}_${moment().format('YYYY-MM-DD')}.pdf`);
}
